// Login modal: HTML5 <dialog> hosting the sign-in paths exposed by the
// kolistat-api Worker (Google, Microsoft, magic link). Built lazily on
// first open so the editor's initial paint isn't blocked.
//
// Dialog lifecycle is `<dialog>.showModal()` / `.close()`. The native
// element handles Escape-to-close, focus trapping, and the backdrop
// pseudo-element; we add a click-outside-to-close handler to match the
// command palette's behaviour. The `required` reason turns all three
// ways to dismiss off: the editor needs an account, so only a sign-in
// (via `closeLoginModal`) takes the dialog away.

import {
  loginWithGoogle,
  loginWithMicrosoft,
  requestMagicLink,
  type AuthErrorInfo,
} from './auth';

const DIALOG_ID = 'login-modal';

/// Why the modal is opening. Drives the heading + context copy and
/// whether the user can dismiss the dialog: `sign-in` (toolbar button)
/// can be closed, `required` (no account, the editor cannot run) cannot.
export type LoginModalReason = 'sign-in' | 'required';

interface ReasonCopy {
  title: string;
  context: string | null;
  dismissible: boolean;
}

const REASON_COPY: Record<LoginModalReason, ReasonCopy> = {
  'sign-in': {
    title: 'sign in to KoliStat',
    context: null,
    dismissible: true,
  },
  'required': {
    title: 'sign in to use the editor',
    context: 'The editor needs a KoliStat account. Accounts are free.',
    dismissible: false,
  },
};

interface ModalRefs {
  dialog: HTMLDialogElement;
  heading: HTMLElement;
  context: HTMLElement;
  intro: HTMLElement;
  sentMsg: HTMLElement;
  emailInput: HTMLInputElement;
  errorMsg: HTMLElement;
  googleBtn: HTMLButtonElement;
  microsoftBtn: HTMLButtonElement;
  linkBtn: HTMLButtonElement;
  closeBtn: HTMLButtonElement;
}

let refs: ModalRefs | null = null;
// Whether the user may dismiss the open dialog (close button, backdrop
// click, Escape). Set per open by `applyReason`; `closeLoginModal`
// sets it back so the `close` listener does not reopen the dialog.
let dismissible = true;

function build(): ModalRefs {
  let dialog = document.getElementById(DIALOG_ID) as HTMLDialogElement | null;
  if (!dialog) {
    dialog = document.createElement('dialog');
    dialog.id = DIALOG_ID;
    document.body.appendChild(dialog);
  }
  dialog.classList.add('login-modal');
  dialog.innerHTML = '';

  const heading = document.createElement('div');
  heading.className = 'login-title';
  heading.textContent = REASON_COPY['sign-in'].title;
  dialog.appendChild(heading);

  const intro = document.createElement('div');
  intro.className = 'login-intro';

  // Optional context line; populated/hidden by applyReason per open.
  const context = document.createElement('div');
  context.className = 'login-context';
  context.hidden = true;
  intro.appendChild(context);

  const googleBtn = document.createElement('button');
  googleBtn.type = 'button';
  googleBtn.className = 'btn login-google';
  googleBtn.textContent = 'Sign in with Google';
  intro.appendChild(googleBtn);

  const microsoftBtn = document.createElement('button');
  microsoftBtn.type = 'button';
  microsoftBtn.className = 'btn login-microsoft';
  microsoftBtn.textContent = 'Sign in with Microsoft';
  intro.appendChild(microsoftBtn);

  const divider = document.createElement('div');
  divider.className = 'login-divider';
  divider.textContent = 'or';
  intro.appendChild(divider);

  const linkForm = document.createElement('form');
  linkForm.className = 'login-link-form';
  linkForm.noValidate = true;

  const emailLabel = document.createElement('label');
  emailLabel.className = 'login-label';
  emailLabel.textContent = 'email';
  emailLabel.htmlFor = 'login-email';
  linkForm.appendChild(emailLabel);

  const emailInput = document.createElement('input');
  emailInput.id = 'login-email';
  emailInput.className = 'login-input';
  emailInput.type = 'email';
  emailInput.placeholder = 'you@example.com';
  emailInput.autocomplete = 'email';
  emailInput.required = true;
  linkForm.appendChild(emailInput);

  const linkBtn = document.createElement('button');
  linkBtn.type = 'submit';
  linkBtn.className = 'btn btn-primary login-magic';
  linkBtn.textContent = 'Send magic link';
  linkForm.appendChild(linkBtn);

  const errorMsg = document.createElement('div');
  errorMsg.className = 'login-error';
  errorMsg.hidden = true;
  linkForm.appendChild(errorMsg);

  intro.appendChild(linkForm);

  const fineprint = document.createElement('div');
  fineprint.className = 'login-fineprint';
  fineprint.append('By creating an account you accept the ');
  fineprint.appendChild(legalLink('https://kolistat.com/terms', 'Terms of service'));
  fineprint.append(' and the ');
  fineprint.appendChild(legalLink('https://kolistat.com/privacy', 'Privacy notice'));
  fineprint.append('.');
  intro.appendChild(fineprint);

  dialog.appendChild(intro);

  const sentMsg = document.createElement('div');
  sentMsg.className = 'login-sent';
  sentMsg.hidden = true;
  dialog.appendChild(sentMsg);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'login-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '×';
  closeBtn.addEventListener('click', () => dialog!.close());
  dialog.appendChild(closeBtn);

  // Click outside the dialog content closes it. The native `<dialog>`
  // still fills the viewport with its backdrop, so a click whose
  // coordinates land on the backdrop reports the dialog element itself
  // as the target.
  dialog.addEventListener('click', (ev) => {
    if (ev.target === dialog && dismissible) dialog!.close();
  });
  // Escape fires `cancel`; preventing it keeps a required dialog open.
  dialog.addEventListener('cancel', (ev) => {
    if (!dismissible) ev.preventDefault();
  });
  // Chrome closes a dialog on a second Escape even when `cancel` was
  // prevented (close watcher rules). Reopen in that case.
  dialog.addEventListener('close', () => {
    if (!dismissible) showDialog(dialog!);
  });

  googleBtn.addEventListener('click', async () => {
    clearError();
    googleBtn.disabled = true;
    const prev = googleBtn.textContent;
    googleBtn.textContent = '… redirecting';
    try {
      await loginWithGoogle();
    } catch (e) {
      googleBtn.disabled = false;
      googleBtn.textContent = prev;
      showError(e instanceof Error ? e.message : String(e));
    }
  });

  microsoftBtn.addEventListener('click', async () => {
    clearError();
    microsoftBtn.disabled = true;
    const prev = microsoftBtn.textContent;
    microsoftBtn.textContent = '… redirecting';
    try {
      await loginWithMicrosoft();
    } catch (e) {
      microsoftBtn.disabled = false;
      microsoftBtn.textContent = prev;
      showError(e instanceof Error ? e.message : String(e));
    }
  });

  linkForm.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    clearError();
    const email = emailInput.value.trim();
    if (!email || !/^.+@.+\..+$/.test(email)) {
      showError('Please enter a valid email address.');
      return;
    }
    linkBtn.disabled = true;
    const prev = linkBtn.textContent;
    linkBtn.textContent = '… sending';
    try {
      await requestMagicLink(email);
      showSent(email);
    } catch (e) {
      showError(e instanceof Error ? e.message : String(e));
    } finally {
      linkBtn.disabled = false;
      linkBtn.textContent = prev;
    }
  });

  return { dialog, heading, context, intro, sentMsg, emailInput, errorMsg, googleBtn, microsoftBtn, linkBtn, closeBtn };
}

function showDialog(dialog: HTMLDialogElement): void {
  if (dialog.open) return;
  if (typeof dialog.showModal === 'function') dialog.showModal();
  else dialog.setAttribute('open', '');
}

function legalLink(href: string, text: string): HTMLAnchorElement {
  const a = document.createElement('a');
  a.href = href;
  a.textContent = text;
  a.target = '_blank';
  a.rel = 'noopener';
  return a;
}

/// Swap heading + context copy and the dismiss rules for the open
/// reason. Called from openLoginModal after resetView so it wins over
/// the default state.
function applyReason(reason: LoginModalReason): void {
  if (!refs) return;
  const copy = REASON_COPY[reason];
  refs.heading.textContent = copy.title;
  if (copy.context) {
    refs.context.textContent = copy.context;
    refs.context.hidden = false;
  } else {
    refs.context.textContent = '';
    refs.context.hidden = true;
  }
  dismissible = copy.dismissible;
  refs.closeBtn.hidden = !copy.dismissible;
}

function showError(msg: string): void {
  if (!refs) return;
  refs.errorMsg.textContent = msg;
  refs.errorMsg.hidden = false;
}

function clearError(): void {
  if (!refs) return;
  refs.errorMsg.hidden = true;
  refs.errorMsg.textContent = '';
}

function showSent(email: string): void {
  if (!refs) return;
  refs.intro.hidden = true;
  refs.sentMsg.hidden = false;
  refs.sentMsg.innerHTML = '';
  const title = document.createElement('div');
  title.className = 'login-sent-title';
  title.textContent = 'Check your email';
  refs.sentMsg.appendChild(title);
  const body = document.createElement('div');
  body.className = 'login-sent-body';
  body.textContent = `We sent a magic link to ${email}. Click it to finish signing in.`;
  refs.sentMsg.appendChild(body);
}

function resetView(): void {
  if (!refs) return;
  refs.intro.hidden = false;
  refs.heading.textContent = REASON_COPY['sign-in'].title;
  refs.context.hidden = true;
  refs.context.textContent = '';
  refs.sentMsg.hidden = true;
  refs.sentMsg.innerHTML = '';
  refs.errorMsg.hidden = true;
  refs.errorMsg.textContent = '';
  refs.googleBtn.disabled = false;
  refs.googleBtn.textContent = 'Sign in with Google';
  refs.microsoftBtn.disabled = false;
  refs.microsoftBtn.textContent = 'Sign in with Microsoft';
  refs.linkBtn.disabled = false;
  refs.linkBtn.textContent = 'Send magic link';
  refs.emailInput.value = '';
}

/// Open the login modal. Lazily builds the dialog on first call. The
/// optional `reason` swaps the heading + context copy; defaults to the
/// plain "sign in" framing used by the toolbar button. A call while the
/// dialog is already open only updates the reason, so a "check your
/// email" view survives a second request for the required dialog.
export function openLoginModal(reason: LoginModalReason = 'sign-in'): void {
  if (!refs) refs = build();
  if (refs.dialog.open) {
    applyReason(reason);
    return;
  }
  resetView();
  applyReason(reason);
  showDialog(refs.dialog);
  refs.emailInput.focus();
}

/// Close the modal if it's currently open. The editor calls this when
/// the auth state turns to signed in, which is the only way a required
/// dialog goes away.
export function closeLoginModal(): void {
  if (!refs) return;
  dismissible = true;
  if (refs.dialog.open) refs.dialog.close();
}

/// Open the modal and render a SEC-004-style auth error (typically
/// `account_exists / different_provider`). Must run AFTER
/// `openLoginModal` since `resetView` clears any prior error banner.
export function showLoginError(err: AuthErrorInfo): void {
  if (!refs) refs = build();
  showDialog(refs.dialog);
  refs.intro.hidden = false;
  refs.sentMsg.hidden = true;
  refs.errorMsg.textContent = humanizeAuthError(err);
  refs.errorMsg.hidden = false;
}

function humanizeAuthError(err: AuthErrorInfo): string {
  if (err.error === 'account_exists' && err.reason === 'different_provider') {
    return 'This email is already registered with a different sign-in method. Use your original method (Google, Microsoft, or magic link) to sign in.';
  }
  if (err.error === 'account_exists') {
    return 'An account with this email already exists. Please sign in with the method you used originally.';
  }
  // Surface the raw code so the user can report it if we hit something unmapped.
  return `Sign-in failed: ${err.error}${err.reason ? ` (${err.reason})` : ''}`;
}
